'use client';

import type { FC, ReactElement, ReactNode, Ref } from 'react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectTriggerSlot,
  SelectValue,
} from '@/app/components/ui/select';
import { cn } from '@/app/utils';
import { CustomTooltip } from './CustomTooltip';

export interface SelectOption {
  value: string;
  label: ReactNode;
  icon?: ReactNode;
  id?: string;
  tooltip?: string | ReactNode;
  disabled?: boolean;
  isVipOption?: boolean;
}

export interface DurationGridSelectProps {
  value: string;
  options: SelectOption[];
  onValueChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  contentClassName?: string;
  cols?: number;
  trigger?: ReactElement;
  /** 受控的面板开合状态；不传时由 Radix 内部自行管理（默认行为不变）。 */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * 绝对定位在选项网格背后的自定义高亮层（如跟随选中项滑动的滑块）。传入时会同时
   * 关掉选项自带的 data-highlighted/data-state=checked 背景色，避免和它重叠；不传时
   * 保持组件原有的静态高亮背景（现有调用方不受影响）。
   */
  highlight?: ReactNode;
  /** 配合 highlight 使用：暴露选项网格容器的 ref，用于测量当前高亮项的位置。 */
  listRef?: Ref<HTMLDivElement>;
  /** 配合 highlight 使用：覆盖选项样式（如 hover 态），由调用方决定与滑块如何分层。 */
  itemClassName?: string;
}

export const DurationGridSelect: FC<DurationGridSelectProps> = ({
  value,
  options,
  onValueChange,
  disabled = false,
  placeholder,
  className,
  contentClassName,
  cols = 8,
  trigger,
  open,
  onOpenChange,
  highlight,
  listRef,
  itemClassName,
}) => {
  const currentOption = options.find((option) => option.value === value);

  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled} open={open} onOpenChange={onOpenChange}>
      <CustomTooltip content={currentOption?.tooltip} side="right" sideOffset={4} disableHoverableContent={true}>
        {trigger ? (
          <SelectTriggerSlot>{trigger}</SelectTriggerSlot>
        ) : (
          <SelectTrigger
            className={cn(
              'relative flex h-[22px] w-fit min-w-[58px] items-center rounded-6 border-none bg-system-white10 px-2 py-0 font-normal text-sm text-system-white hover:border-system-white64 [&>span]:overflow-visible',
              className,
            )}
          >
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
        )}
      </CustomTooltip>
      <SelectContent
        className={cn(
          'z-40 w-fit rounded-8 border border-system-white08 bg-[rgba(34,37,42,0.64)] p-1 shadow-[0px_4px_20px_0px_rgba(0,0,0,0.48)] backdrop-blur-[30px]',
          contentClassName,
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=open]:animate-in',
        )}
      >
        <div ref={listRef} className="relative">
          {highlight}
          <SelectGroup className="grid gap-y-1" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
            {options.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className={cn(
                  'relative z-[1] flex h-[34px] w-[34px] items-center justify-center p-1 text-sm leading-none data-[state=unchecked]:text-system-white48 [&>span]:w-full [&>span]:overflow-visible',
                  highlight
                    ? itemClassName
                    : 'data-[highlighted]:bg-system-white08 data-[state=checked]:bg-system-white08',
                  option.isVipOption &&
                    'bg-gradient-to-b from-[#7280FF] via-[#8C97FF] to-[#DADFFF] data-[state=checked]:from-[#5263FF] data-[state=checked]:via-[#6473FF] data-[state=checked]:to-[#7784ff] data-[state=unchecked]:bg-clip-text data-[state=unchecked]:text-transparent',
                )}
              >
                <div className="whitespace-nowrap text-center">{option.label}</div>
              </SelectItem>
            ))}
          </SelectGroup>
        </div>
      </SelectContent>
    </Select>
  );
};
